import type { AdminPick, AdminPickPayload } from './adminPicksApi';
import { apiRequest } from './api';
import type { IntakeDraft, IntakeField } from './intakeApi';

/** Weekly discovery admin (PRD §6.4). */

export type CandidateStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface PickCandidate {
  id: string;
  runId: string;
  draft: IntakeDraft & { confidences: Record<IntakeField, number> };
  reviewSummary: string | null;
  whyPickedDraftCN: string | null;
  whyPickedDraftEN: string | null;
  confidence: number | null;
  status: CandidateStatus;
  rejectionReason: string | null;
  createdPickId: string | null;
  createdAt: string;
  run?: { weekKey: string };
}

export interface DiscoveryRun {
  id: string;
  weekKey: string;
  status: 'RUNNING' | 'DONE' | 'FAILED';
  startedAt: string;
  finishedAt: string | null;
  topicCount: number;
  candidateCount: number;
  costMicroUsd: number | null;
  error: string | null;
}

export interface DiscoveryTopic {
  id: string;
  region: string | null;
  stage: string;
  category: string | null;
  enabled: boolean;
  note: string | null;
}

export type TopicInput = Omit<DiscoveryTopic, 'id'>;

export const discoveryApi = {
  async candidates(status: CandidateStatus = 'PENDING'): Promise<PickCandidate[]> {
    return (await apiRequest<{ data: PickCandidate[] }>(`/admin/picks/candidates?status=${status}`))
      .data;
  },
  async candidate(id: string): Promise<PickCandidate> {
    return (await apiRequest<{ data: PickCandidate }>(`/admin/picks/candidates/${id}`)).data;
  },
  async approve(id: string, payload: AdminPickPayload): Promise<AdminPick> {
    return (
      await apiRequest<{ data: AdminPick }>(`/admin/picks/candidates/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify(payload),
      })
    ).data;
  },
  async reject(id: string, reason: string) {
    return apiRequest(`/admin/picks/candidates/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },
  async runs(): Promise<DiscoveryRun[]> {
    return (await apiRequest<{ data: DiscoveryRun[] }>('/admin/picks/discovery/runs')).data;
  },
  async runNow() {
    return apiRequest('/admin/picks/discovery/run', { method: 'POST' });
  },
  async topics(): Promise<DiscoveryTopic[]> {
    return (await apiRequest<{ data: DiscoveryTopic[] }>('/admin/picks/topics')).data;
  },
  async createTopic(t: TopicInput) {
    return apiRequest('/admin/picks/topics', { method: 'POST', body: JSON.stringify(t) });
  },
  async updateTopic(id: string, t: TopicInput) {
    return apiRequest(`/admin/picks/topics/${id}`, { method: 'PUT', body: JSON.stringify(t) });
  },
  async deleteTopic(id: string) {
    return apiRequest(`/admin/picks/topics/${id}`, { method: 'DELETE' });
  },
};
